import { newId } from './ids.js';
import { clearStored, readStored, writeStored } from './storage.js';

const VISITOR_KEY = 'id';

let visitorId: string | null | undefined;
let pageLoadKey: string | undefined;

/** Not a visitor: it dies with the page load, which is why the envelope never reports it. */
export function getPageLoadKey(): string {
  pageLoadKey ??= newId();
  return pageLoadKey;
}

/**
 * Only ever called with consent, since reading the device needs it just as writing does.
 *
 * Null when storage refuses to keep it: an in-memory fallback would look durable downstream
 * and split one visitor into one per page load.
 */
export function getOrCreateVisitorId(): string | null {
  if (visitorId !== undefined) {
    return visitorId;
  }
  const stored = readStored(VISITOR_KEY);
  if (stored) {
    visitorId = stored;
    return visitorId;
  }
  // Adopts the key this page load already bucketed on, so granting consent does not re-bucket.
  const key = getPageLoadKey();
  // A stubbed store accepts the write and keeps nothing; read back rather than trust it.
  visitorId = writeStored(VISITOR_KEY, key) && readStored(VISITOR_KEY) === key ? key : null;
  return visitorId;
}

/** @internal — test-only: drops the cache but keeps storage, i.e. what a reload does. */
export function _forgetVisitorCache() {
  visitorId = undefined;
  pageLoadKey = undefined;
}

/** @internal — test-only reset */
export function _resetVisitor() {
  _forgetVisitorCache();
  clearStored(VISITOR_KEY);
}
